import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ScrollText, Search, Plus, Filter, ChevronRight, AlertOctagon, ShieldAlert,
  Users, Loader2, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api, apiErrorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  VLKPageHeader, VLKCard, VLKKPICard, VLKButton, VLKInput, VLKSelect,
  VLKTextarea, VLKLabel, VLKAlertDialog, VLKStatusBadge, VLKEmptyState,
  toast,
} from "@/components/vlk";
import {
  severityChip, SlaChip,
} from "@/components/liveAudit/audit-utils";

const STATUS_STYLES = {
  OPEN:       "text-warning border-warning/30 bg-warning/5",
  IN_REVIEW:  "text-info border-info/30 bg-info/5",
  RESOLVED:   "text-success border-success/30 bg-success/5",
  DISMISSED:  "text-muted-foreground border-border bg-secondary/40",
};

const CATEGORY_LABELS = {
  fraud: "Fraud", security: "Security", compliance: "Compliance",
  recovery: "Recovery", operational: "Operational", other: "Other",
};

const MANAGE_ROLES = new Set(["system_admin", "compliance_officer", "security_officer", "institution_admin"]);

export default function Cases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && MANAGE_ROLES.has(user.role);
  const [filters, setFilters] = useState({ status: "", priority: "", category: "", q: "" });
  const [showCreate, setShowCreate] = useState(false);

  const { data: kpis } = useQuery({
    queryKey: ["cases", "kpis"],
    queryFn: () => api.get("/cases/kpis").then((r) => r.data),
  });
  const { data, isLoading } = useQuery({
    queryKey: ["cases", "list", filters],
    queryFn: () => api.get("/cases", {
      params: {
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        category: filters.category || undefined,
        q: filters.q || undefined,
        limit: 200,
      },
    }).then((r) => r.data),
  });

  const items = data?.items || [];

  return (
    <div data-testid="cases-page">
      <VLKPageHeader
        title="Case management"
        description="Investigation containers for fraud, security, and compliance work. Every mutation writes the audit ledger and alerts the assignee."
        actions={canManage && (
          <VLKButton size="md" data-testid="cases-new" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" /> New case
          </VLKButton>
        )}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <VLKKPICard testId="cases-kpi-open" label="Open" value={kpis?.open ?? "—"} icon={Clock} tone="warning" />
        <VLKKPICard testId="cases-kpi-review" label="In review" value={kpis?.in_review ?? "—"} icon={Users} tone="info" />
        <VLKKPICard testId="cases-kpi-critical" label="Critical active" value={kpis?.critical_active ?? "—"} icon={AlertOctagon} tone="danger" />
        <VLKKPICard testId="cases-kpi-resolved" label="Resolved" value={kpis?.resolved ?? "—"} icon={CheckCircle2} tone="success" />
      </div>

      {/* Filter bar */}
      <VLKCard className="mb-4">
        <div className="p-3 flex flex-wrap gap-2 items-center border-b border-border">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <VLKInput
              data-testid="cases-search"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Search title, description, case_id…"
              className="pl-8"
            />
          </div>
          <VLKSelect
            data-testid="cases-status-filter"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="max-w-[160px]"
          >
            <option value="">All status</option>
            <option value="OPEN">Open</option>
            <option value="IN_REVIEW">In review</option>
            <option value="RESOLVED">Resolved</option>
            <option value="DISMISSED">Dismissed</option>
          </VLKSelect>
          <VLKSelect
            data-testid="cases-priority-filter"
            value={filters.priority}
            onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}
            className="max-w-[140px]"
          >
            <option value="">All priority</option>
            <option value="low">Low</option><option value="medium">Medium</option>
            <option value="high">High</option><option value="critical">Critical</option>
          </VLKSelect>
          <VLKSelect
            data-testid="cases-category-filter"
            value={filters.category}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
            className="max-w-[170px]"
          >
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </VLKSelect>
        </div>

        {/* List */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 flex items-center justify-center text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading cases…
            </div>
          ) : items.length === 0 ? (
            <VLKEmptyState
              icon={ScrollText}
              title="No cases match the current filters"
              description="Adjust the filters or open a new case."
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  {["Case", "Title", "Category", "Priority", "SLA", "Assignee", "Opened", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.case_id}
                    data-testid={`case-row-${c.case_id}`}
                    className="border-b border-border last:border-0 hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/cases/${c.case_id}`)}
                  >
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{c.case_id}</td>
                    <td className="px-4 py-2 font-medium max-w-[420px] truncate">{c.title}</td>
                    <td className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">{c.category}</td>
                    <td className="px-4 py-2">{severityChip(c.priority)}</td>
                    <td className="px-4 py-2">{c.status !== "RESOLVED" && c.status !== "DISMISSED" ? <SlaChip status={c.sla_status} /> : <span className="text-muted-foreground text-[10px]">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground truncate max-w-[180px]">{c.assignee_user_id}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{new Date(c.opened_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2">
                      <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-wider", STATUS_STYLES[c.status])}>
                        {c.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground"><ChevronRight className="h-3.5 w-3.5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </VLKCard>

      <CreateCaseDialog open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}

function CreateCaseDialog({ open, onClose }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", category: "compliance", priority: "medium" });
  const mut = useMutation({
    mutationFn: (payload) => api.post("/cases", payload).then((r) => r.data),
    onSuccess: (c) => {
      toast.success("Case opened", { description: c.case_id });
      qc.invalidateQueries({ queryKey: ["cases"] });
      onClose();
      navigate(`/cases/${c.case_id}`);
    },
    onError: (e) => toast.error("Could not open case", { description: apiErrorText(e) }),
  });

  return (
    <VLKAlertDialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Open a new case"
      actions={
        <>
          <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
          <VLKButton
            data-testid="cases-create-submit"
            loading={mut.isPending}
            disabled={!form.title.trim() || form.description.trim().length < 3}
            onClick={() => mut.mutate(form)}
          >
            Open case
          </VLKButton>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <VLKLabel>Title</VLKLabel>
          <VLKInput
            data-testid="cases-create-title"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Short summary"
          />
        </div>
        <div>
          <VLKLabel>Description</VLKLabel>
          <VLKTextarea
            data-testid="cases-create-description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Detailed rationale, links, initial evidence…"
            rows={4}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <VLKLabel>Category</VLKLabel>
            <VLKSelect
              data-testid="cases-create-category"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            >
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </VLKSelect>
          </div>
          <div>
            <VLKLabel>Priority</VLKLabel>
            <VLKSelect
              data-testid="cases-create-priority"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            >
              <option value="low">Low</option><option value="medium">Medium</option>
              <option value="high">High</option><option value="critical">Critical</option>
            </VLKSelect>
          </div>
        </div>
      </div>
    </VLKAlertDialog>
  );
}
